import { useEffect, useRef, useState } from "react";
import { BsBell } from "react-icons/bs";
import "./notification.scss";
import WebService from "../../Services/WebService";
import { toast } from "react-toastify";
import HelperService from "../../Services/HelperService";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useLocation, useParams } from "react-router-dom";
import { profileCompletionFlags } from "../../assets/interface/primeSharkWebsite_interface";
import ConfirmSubscriptionModal from "../Login/Subscription/BuySubscriptionApprovalModal";
import { SUBSCRIPTION_BUY_MODE } from "../../assets/constants/constants";
import DocumentAccessRequestsModal from "../Profile/DocumentAccessRequestsModal";

interface Notification {
  id: string;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

interface PropsData {
  count: number;
}

const NotificationBell = ({ count }: PropsData) => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const ref = useRef<HTMLDivElement>(null);
  const activeLoginTypes = useSelector(
    (state: any) => state?.items?.storeActiveLoginType,
  );
  const bodyRef = useRef<HTMLDivElement>(null);
  let account_type = activeLoginTypes?.account_type;
  let login_type = activeLoginTypes?.loginType;
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [fetchingMore, setFetchingMore] = useState(false);
  let navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [notCount, setNoteCount] = useState(0);
  const storeProfileCompletion: profileCompletionFlags = useSelector(
    (state: any) => state?.items?.profileCompletionFlags,
  );
  let {
    has_investment_profile_completed,
    has_investment_subscription,
    has_want_to_sell_subscription,
    has_want_to_sell_profile_completed,
    has_want_to_grow_subscription,
    has_want_to_grow_profile_completed,
    has_active_subscription,
    has_free_subscription,
    has_investment_free_subscription,
    has_want_to_grow_subs,
    has_investor_free_subscription,
    has_investor_profile_completed,
    has_investor_subscription,
    has_subscription_expired,
    has_want_to_grow_free_subscription,
    has_investor_subs,
    has_investment_subs,
    has_want_to_sell_subs,
    has_want_to_sell_free_subscription,
  } = storeProfileCompletion;
  const [modalMessage, setModalMessage] = useState("");
  const [subscriptionMode, setSubscriptionMode] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showDocAccessModal, setShowDocAccessModal] = useState(false);
  const [docActionLoading, setDocActionLoading] = useState<Record<string, boolean>>({});

  const handleRespondAccess = (e: React.MouseEvent, requestId: string | number, status: "ACCEPTED" | "REJECTED") => {
    e.stopPropagation();
    if (!requestId) return;
    setDocActionLoading((prev) => ({ ...prev, [String(requestId)]: true }));
    WebService.postAPI({
      action: "document-access/respond",
      body: {
        request_id: requestId,
        status: status,
      },
    })
      .then((res: any) => {
        if (res?.status) {
          toast.success(res?.message || `Access request ${status.toLowerCase()} successfully.`);
          getNotificationList(page);
        } else {
          toast.error(res?.message || "Failed to update access request.");
        }
      })
      .catch((err: any) => {
        toast.error(err?.response?.data?.message || "Failed to update access request.");
      })
      .finally(() => {
        setDocActionLoading((prev) => ({ ...prev, [String(requestId)]: false }));
      });
  };
  useEffect(() => {
    setNoteCount(count);
  }, [count]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const getNotificationList = (pageNumber: number, isLoadMore = false) => {
    if (!hasMore && isLoadMore) return;

    if (isLoadMore) {
      setFetchingMore(true);
    } else {
      setLoader(true);
    }

    return WebService.CommonApi({
      method: "GET",
      action: `notifications?offset=${pageNumber - 1}`,
      body: null,
    })
      .then((res: any) => {
        if (res?.status) {
          const newList = res?.list || [];

          if (isLoadMore) {
            setNotifications((prev) => [...prev, ...newList]);
          } else {
            setNotifications(newList);
          }

          // 👇 Important: check if more data available
          if (res?.next_offset === -1) {
            setHasMore(false);
          } else {
            setHasMore(true);
          }
        }
      })
      .catch((error: any) => {
        toast.error(error?.response?.data?.message);
      })
      .finally(() => {
        setLoader(false);
        setFetchingMore(false);
      });
  };
  const handleNavigation = (
    notification_id: string,
    action: string,
    action_id: string,
  ) => {
    // Mentor-flow actions bypass the subscription gate: the user already
    // paid per-session to participate, so they should always be able to read
    // the notification.
    if (action === "MENTOR_CHAT_VIEW") {
      setOpen(false);
      if (login_type === "entrepreneur") {
        navigate(`/entrepreneur/${account_type}/chats/${action_id}`, {
          replace: true,
        });
      } else {
        navigate(`/investor/chats/${action_id}`, { replace: true });
      }
      handleMarkRead(notification_id);
      return;
    }
    if (action === "MENTOR_BOOKING_VIEW") {
      setOpen(false);
      navigate(`/my-sessions`, { replace: true });
      handleMarkRead(notification_id);
      return;
    }

    if (action === "DOCUMENT_ACCESS_REQUEST") {
      setOpen(false);
      handleMarkRead(notification_id);
      setShowDocAccessModal(true);
      return;
    }

    if (action === "PROFILE_VIEW") {
      if (login_type === "investor") {
        setOpen(false);
        navigate(`/investor/my-profile`, {
          replace: true,
        });
      } else {
        navigate(`/${login_type}/${account_type}/my-profile`, {
          replace: true,
        });
      }
      handleMarkRead(notification_id);
      setOpen(!open);
    } else if (
      has_active_subscription &&
      (has_investment_subs ||
        has_want_to_grow_subs ||
        has_want_to_sell_subs ||
        has_investor_free_subscription)
    ) {
      if (action === "CHAT_VIEW") {
        if (login_type === "entrepreneur") {
          setOpen(false);
          navigate(`/entrepreneur/${account_type}/chats/${action_id}`, {
            replace: true,
          });
        } else {
          setOpen(false);
          navigate(`/${account_type}/chats/${action_id}`, { replace: true });
        }
      }
      if (action === "PROPOSAL_VIEW") {
        if (login_type === "investor") {
          setOpen(false);
          navigate(`/investor/my-proposals`, {
            replace: true,
          });
        } else {
          navigate(`/${login_type}/${account_type}/sent-proposals`, {
            replace: true,
          });
        }
      }

      handleMarkRead(notification_id);
      setOpen(!open);
    } else {
      setSubscriptionMode(SUBSCRIPTION_BUY_MODE.NEW);
      if (!has_active_subscription) {
        setModalMessage(
          `You don't have any subscription plan! Buy a paid plan to access this feature and enjoy additional benefits.`,
        );
      } else {
        setModalMessage(
          `You’re currently on a free plan, Upgrade to paid plan to access this feature and enjoy additional benefits.`,
        );
      }
      setShowModal(true);
    }
  };
  const handleMarkRead = (notification_id: string) => {
    return WebService.CommonApi({
      method: "PUT",
      action: `notification/${notification_id}/read`,
      body: null,
    })
      .then((res: any) => {
        if (res?.status) {
          setNoteCount(res?.unread_count);
        }
      })
      .catch((error: any) => {
        toast.error(error?.response?.data?.message);
        return error;
      })
      .finally(() => {
        setLoader(false);
      });
  };
  const handleReadAll = () => {
    WebService.addLoader("read_all");
    return WebService.CommonApi({
      method: "POST",
      action: `notification/read/all`,
      body: null,
    })
      .then((res: any) => {
        if (res?.status) {
          setNoteCount(0);
          // Flip every visible row to read so the unread highlighting
          // disappears immediately without needing a refetch.
          setNotifications((prev: any) =>
            prev.map((n: any) => ({ ...n, is_read: true })),
          );
        }
      })
      .catch((error: any) => {
        toast.error(error?.response?.data?.message);
        return error;
      })
      .finally(() => {
        WebService.removeLoader("read_all");
      });
  };

  const handleDeleteNotification = (notification_id: string) => {
    WebService.addLoader(`delete_${notification_id}`);
    return WebService.CommonApi({
      method: "DELETE",
      action: `notification/${notification_id}`,
      body: null,
    })
      .then((res: any) => {
        if (res?.status) {
          setNoteCount(res?.unread_count);
          getNotificationList(page);
        }
      })
      .catch((error: any) => {
        toast.error(error?.response?.data?.message);
        return error;
      })
      .finally(() => {
        WebService.removeLoader(`delete_${notification_id}`);
      });
  };
  const handleYes = () => {
    navigate(
      `/${login_type?.toLowerCase()}/${account_type?.toLowerCase()}/subscriptions?mode=${subscriptionMode}`,
    );
  };
  useEffect(() => {
    if (!open) return;

    const container = bodyRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;

      if (scrollTop + clientHeight >= scrollHeight - 10) {
        if (!fetchingMore && hasMore) {
          const nextPage = page + 1;

          console.log("🔥 calling next page:", nextPage);

          setPage(nextPage);
          getNotificationList(nextPage, true);
        }
      }
    };

    container.addEventListener("scroll", handleScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [open, page, hasMore, fetchingMore]);
  return (
    <>
      <div
        className="notification-wrapper me-3"
        ref={ref}
        onClick={() => getNotificationList(page)}
      >
        <div
          className="bell"
          onClick={() => {
            setOpen(!open);
            setPage(1);
            setHasMore(true);
            getNotificationList(1, false);
          }}
        >
          <BsBell size={24} />
          {notCount > 0 && (
            <span className="badge1">{notCount > 10 ? "10+" : notCount}</span>
          )}
        </div>

        {open && (
          <div className="notification-dropdown">
            <div className="notification-header justify-content-end border-bottom-0">
              {notifications.length > 0 && (
                <button
                  id="read_all"
                  className="read-all-btn"
                  onClick={handleReadAll}
                >
                  Read all
                </button>
              )}
            </div>

            {/* 📜 Content */}
            <div className="notification-body" ref={bodyRef}>
              {loader ? (
                <div className="notification-loader">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="skeleton-item">
                      <div className="skeleton-title" />
                      <div className="skeleton-line" />
                    </div>
                  ))}
                </div>
              ) : notifications.length === 0 ? (
                <p className="empty">No notifications</p>
              ) : (
                notifications.map((n: any) => (
                  <div
                    key={n.id}
                    className={`notification-item ${!n.is_read ? "unread" : ""}`}
                    title={n?.notification?.description || n?.notification?.title || ""}
                    onClick={() => {
                      console.log(n);

                      handleNavigation(
                        n?.id,
                        n?.notification?.action,
                        n?.notification?.action_id,
                      );
                    }}
                  >
                    <div className="notification-content">
                      <h6 title={n?.notification?.title || ""}>{n?.notification?.title}</h6>

                      <p title={n?.notification?.description || ""}>
                        {n?.notification?.description
                          ? n.notification.description.length > 50
                            ? n.notification.description.slice(0, 50) + "..."
                            : n.notification.description
                          : ""}
                      </p>

                      {n?.notification?.action === "DOCUMENT_ACCESS_REQUEST" && (
                        <div className="d-flex align-items-center gap-1 my-1">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary py-0 px-2 font-11"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpen(false);
                              handleMarkRead(n?.id);
                              setShowDocAccessModal(true);
                            }}
                          >
                            Show details
                          </button>
                        </div>
                      )}

                      <span>
                        {n.created_at
                          ? HelperService.getFormatedDateByDMYTime(
                            n?.created_at,
                          )
                          : null}
                      </span>
                    </div>

                    {/* 🗑️ Delete icon */}
                    <button
                      id={`delete_${n?.id}`}
                      className="delete-single-btn"
                      onClick={(e) => {
                        e.stopPropagation(); // ❗ prevent navigation
                        handleDeleteNotification(n.id);
                      }}
                      title="Delete notification"
                    >
                      ❌
                    </button>
                  </div>
                ))
              )}
              {fetchingMore && (
                <div className="notification-loader">
                  <div className="skeleton-item">
                    <div className="skeleton-title" />
                    <div className="skeleton-line" />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {showModal && (
        <ConfirmSubscriptionModal
          show={showModal}
          message={modalMessage}
          onConfirm={handleYes}
          onCancel={() => setShowModal(false)}
        />
      )}
      {showDocAccessModal && (
        <DocumentAccessRequestsModal
          show={showDocAccessModal}
          onHide={() => setShowDocAccessModal(false)}
        />
      )}
    </>
  );
};

export default NotificationBell;
